/**
 * Integer percentages that sum to exactly 100 (largest remainder method).
 */
export function distributeRoundedPercentages(weights: number[]): number[] {
  if (weights.length === 0) {
    return [];
  }

  const safeWeights = weights.map((weight) => (Number.isFinite(weight) ? Math.max(0, weight) : 0));
  const totalWeight = safeWeights.reduce((sum, weight) => sum + weight, 0);

  if (totalWeight <= 0) {
    return safeWeights.map(() => 0);
  }

  const rawPercentages = safeWeights.map((weight) => (weight / totalWeight) * 100);
  const floors = rawPercentages.map((value) => Math.floor(value));
  const remainder = 100 - floors.reduce((sum, value) => sum + value, 0);

  const ranked = rawPercentages
    .map((value, index) => ({ index, fractional: value - Math.floor(value) }))
    .sort((a, b) => b.fractional - a.fractional || a.index - b.index);

  const result = [...floors];
  for (let i = 0; i < remainder; i += 1) {
    result[ranked[i % ranked.length].index] += 1;
  }

  return result;
}

/** Format an integer (or already-rounded) allocation percent for Dashboard / Assets / Investments. */
export function formatIntegerPercent(value: number): string {
  const safe = Number.isFinite(value) ? Math.round(value) : 0;
  return `${safe}%`;
}
